import { ChangeDetectionStrategy, Component, Inject, OnInit } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection, RoleConnectionBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogWithDiscardConfirmationComponent } from "@common/ux/adapt-common-dialog/base-dialog-with-discard-confirmation.component/base-dialog-with-discard-confirmation.component";
import { CoachService } from "app/features/coaches/coach.service";
import { lastValueFrom } from "rxjs";

@Component({
    selector: "adapt-coach-connection-dialog",
    templateUrl: "./coach-connection-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CoachConnectionDialogComponent extends BaseDialogWithDiscardConfirmationComponent<Connection> implements OnInit {
    public readonly dialogName = "CoachConnection";
    protected readonly autoResolveData = this.connection;

    public isNew: boolean;

    public coachRoles: Role[] = [];
    private changedEntities: RoleConnection[] = [];

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public connection: Connection,
        protected commonDataService: CommonDataService,
        private coachService: CoachService,
    ) {
        super();

        this.isNew = connection.entityAspect.entityState.isAdded();
    }

    public get entitiesToConfirm() {
        return [
            this.connection,
            ...this.changedEntities,
        ];
    }

    public get entitiesAreUnmodifiedOrInvalid() {
        return this.connection.roleConnections.length === 0
            || super.entitiesAreUnmodifiedOrInvalid;
    }

    public async ngOnInit() {
        // find the coach role that we will attach to a new role connection
        this.coachRoles = (await lastValueFrom(this.coachService.getAllCoachRolesForOrganisation(this.connection.organisationId)))
            .sort(SortUtilities.getSortByFieldFunction<Role>("roleType.code"));

        if (this.isNew) {
            const coachRole = this.coachRoles.find((r) => r.isActive() && r.roleType?.code === RoleTypeCode.Coach);
            if (!coachRole) {
                throw new Error("Coach role does not exist");
            }

            const roleConnection = await this.createRoleConnection(coachRole);
            ArrayUtilities.addElementIfNotAlreadyExists(this.changedEntities, roleConnection);
        }
    }

    public isRoleAssigned(role: Role) {
        return !!this.getExistingRoleConnection(role);
    }

    public async onCoachRoleChanged(role: Role, value?: boolean | null) {
        if (value) {
            await this.addRoleConnection(role);
        } else {
            await this.removeRoleConnection(role);
        }
    }

    private async addRoleConnection(role: Role) {
        // restore previously deleted role connection if exists
        const deletedRoleConnection = this.changedEntities.find((rc) => rc.roleId === role.roleId && rc.entityAspect.entityState.isDeleted());
        if (deletedRoleConnection) {
            ArrayUtilities.removeElementFromArray(deletedRoleConnection, this.changedEntities);
            deletedRoleConnection.entityAspect.rejectChanges();
            return;
        }

        // create role connection if not exists
        const coachRoleConnection = await this.createRoleConnection(role);
        ArrayUtilities.addElementIfNotAlreadyExists(this.changedEntities, coachRoleConnection);
    }

    private async removeRoleConnection(role: Role) {
        const roleConnection = this.getExistingRoleConnection(role);
        if (!roleConnection) {
            return;
        }

        if (roleConnection.entityAspect.entityState.isAdded()) {
            ArrayUtilities.removeElementFromArray(roleConnection, this.changedEntities);
        } else {
            ArrayUtilities.addElementIfNotAlreadyExists(this.changedEntities, roleConnection);
        }
        await lastValueFrom(this.commonDataService.remove(roleConnection));
    }

    private getExistingRoleConnection(role: Role) {
        return this.connection.roleConnections.find((rc) => rc.isActive() && rc.roleId === role.roleId);
    }

    private async createRoleConnection(role: Role) {
        return await lastValueFrom(this.commonDataService.create(RoleConnectionBreezeModel, {
            roleId: role.roleId,
            connectionId: this.connection.connectionId,
            startDate: this.connection.startDate,
        }));
    }
}
